import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import type { RefreshResponse } from "@/types/api";

const baseURL = import.meta.env.VITE_API_URL || "";

let accessToken: string | null = null;
let refreshing: Promise<string | null> | null = null;
let onUnauthorized: (() => void) | null = null;

export const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true, // чтобы httpOnly refresh-cookie ходила
});

export function setAccessToken(t: string | null) {
  accessToken = t;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onAuthLost(cb: () => void) {
  onUnauthorized = cb;
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken && !config.headers.has("Authorization")) {
    config.headers.set("Authorization", `Bearer ${accessToken}`);
  }
  return config;
});

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !original.url?.includes("/api/v1/auth/")
    ) {
      original._retry = true;
      const newToken = await refreshAccess();
      if (newToken) {
        original.headers.set("Authorization", `Bearer ${newToken}`);
        return api.request(original);
      }
      onUnauthorized?.();
    }
    return Promise.reject(error);
  },
);

async function refreshAccess(): Promise<string | null> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const r = await axios.post<RefreshResponse>(
        `${baseURL}/api/v1/auth/refresh`,
        {},
        { withCredentials: true },
      );
      accessToken = r.data.access;
      return accessToken;
    } catch {
      accessToken = null;
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}
